import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Copy, MessageCircle, Share2, Heart, Sparkles, PartyPopper } from 'lucide-react';
import MobileLayout from '../components/MobileLayout';
import ProtectedRoute from '../components/ProtectedRoute';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Pre-computed confetti positions for consistent rendering
const confettiPieces = Array.from({ length: 50 }, (_, i) => ({
  id: i,
  left: (i * 2) % 100,
  delay: (i * 0.04) % 2,
  duration: 2 + ((i * 0.08) % 2),
  color: ['#FF6B9D', '#FFB74D', '#4CAF50', '#2196F3', '#9C27B0', '#FF5722'][i % 6],
  rotation: (i * 7.2) % 360,
}));

// Celebration confetti animation
const Confetti = () => (
  <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
    {confettiPieces.map((piece) => (
      <div
        key={piece.id}
        className="absolute animate-confetti"
        style={{
          left: `${piece.left}%`,
          animationDelay: `${piece.delay}s`,
          animationDuration: `${piece.duration}s`,
        }}
      >
        <div 
          className="w-3 h-3 rounded-sm"
          style={{
            backgroundColor: piece.color,
            transform: `rotate(${piece.rotation}deg)`,
          }}
        />
      </div>
    ))}
  </div>
);

// Partner connected celebration overlay
const CelebrationOverlay = ({ partnerName, onComplete }) => {
  useEffect(() => {
    const timer = setTimeout(onComplete, 4000);
    return () => clearTimeout(timer);
  }, [onComplete]);
  
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center animate-in fade-in duration-300">
      <Confetti />
      <div className="bg-white rounded-3xl p-8 mx-6 text-center animate-in zoom-in duration-500 max-w-sm relative overflow-hidden">
        <div className="absolute -top-10 -left-10 w-32 h-32 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-orange-200/30 rounded-full blur-3xl" />
        
        <div className="relative">
          <div className="flex justify-center gap-2 mb-4">
            <PartyPopper className="w-10 h-10 text-yellow-500 animate-bounce" />
            <Sparkles className="w-10 h-10 text-primary animate-pulse" />
            <PartyPopper className="w-10 h-10 text-yellow-500 animate-bounce" style={{ animationDelay: '0.2s' }} />
          </div>
          
          <h2 className="text-3xl font-bold text-gradient mb-2">Congratulations!</h2>
          <p className="text-xl text-gray-700 mb-4">You&apos;re now connected!</p>
          
          <div className="flex justify-center -space-x-4 my-6">
            <div className="w-16 h-16 gradient-brand rounded-full flex items-center justify-center border-4 border-white shadow-lg">
              <Heart className="w-8 h-8 text-white" fill="white" />
            </div>
            <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-pink-500 rounded-full flex items-center justify-center border-4 border-white shadow-lg">
              <Heart className="w-8 h-8 text-white" fill="white" />
            </div>
          </div>
          
          <p className="text-gray-600">
            You and <span className="font-semibold text-primary">{partnerName}</span> are ready to grow together! 💕
          </p>
          
          <p className="text-sm text-gray-400 mt-4">
            Start your journey of love and connection...
          </p>
        </div>
      </div>
    </div>
  );
};

const ConnectionContent = ({ user }) => {
  const [searchParams] = useSearchParams();
  const codeFromUrl = searchParams.get('code') || searchParams.get('c') || '';
  const storedPendingCode = typeof window !== 'undefined' ? localStorage.getItem('pending_partner_code') || '' : '';
  const initialPartnerCode = (codeFromUrl || storedPendingCode).replace(/\D/g, '').slice(0, 6);

  const [connectionCode, setConnectionCode] = useState('');
  const [partnerCode, setPartnerCode] = useState(initialPartnerCode);
  const [loading, setLoading] = useState(false);
  const [connectionInfo, setConnectionInfo] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [newPartnerName, setNewPartnerName] = useState('');
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  
  useEffect(() => {
    fetchConnectionInfo();
  }, []);

  // If code is in URL or localStorage, make sure state is synchronized
  useEffect(() => {
    if (!partnerCode && initialPartnerCode) {
      setPartnerCode(initialPartnerCode);
    }
  }, [initialPartnerCode, partnerCode]);
  
  // Check if we need to show celebration for user who received connection (user 1)
  useEffect(() => {
    if (connectionInfo?.has_partner && connectionInfo?.partner?.name) {
      // Check if this user has ever seen the partner connected celebration
      const celebrationKey = `partnerCelebration_${user?.user_id}`;
      const hasSeenCelebration = localStorage.getItem(celebrationKey);
      
      if (!hasSeenCelebration) {
        // This user hasn't seen the celebration yet - show it!
        setNewPartnerName(connectionInfo.partner.name.split(' ')[0]);
        setShowCelebration(true);
        localStorage.setItem(celebrationKey, 'true');
      }
    }
  }, [connectionInfo, user?.user_id]);
  
  const fetchConnectionInfo = async () => {
    try {
      const response = await axios.get(`${API}/user/connection-info`, {
        withCredentials: true
      });
      setConnectionCode(response.data.connection_code);
      setConnectionInfo(response.data);
    } catch (error) {
      console.error('Error fetching connection info:', error);
    }
  };

  const getInviteMessage = (code) => {
    const link = `https://coupleinapp.com/connect?code=${code}`;
    return `Hey! Let's connect on CoupleIn 💕\n\nTap here to connect with me:\n${link}\n\nMy invite code is: ${code}`;
  };
  
  const copyCode = () => {
    const message = getInviteMessage(connectionCode);
    navigator.clipboard.writeText(message);
    toast.success('Invite link & code copied!');
  };
  
  const shareWhatsApp = () => {
    const message = getInviteMessage(connectionCode);
    const link = `https://coupleinapp.com/connect?code=${connectionCode}`;
    
    if (navigator.share) {
      navigator.share({
        title: 'Connect on CoupleIn',
        text: message,
        url: link
      }).catch(() => {
        window.open(`https://web.whatsapp.com/send?text=${encodeURIComponent(message)}`, '_blank');
      });
    } else {
      window.open(`https://web.whatsapp.com/send?text=${encodeURIComponent(message)}`, '_blank');
    }
  };
  
  const connectPartner = async () => {
    if (!partnerCode || partnerCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/connect/partner`,
        { code: partnerCode },
        { withCredentials: true, params: { code: partnerCode } }
      );
      
      // Clean up stored pending code
      try {
        localStorage.removeItem('pending_partner_code');
      } catch (e) {
        /* ignore */
      }

      // Show celebration animation for user who entered the code (user 2)
      setNewPartnerName(response.data.partner_name);
      setShowCelebration(true);
      
      // Mark this user as having seen the celebration
      const celebrationKey = `partnerCelebration_${user?.user_id}`;
      localStorage.setItem(celebrationKey, 'true');
      
      // IMPORTANT: Refresh the auth context to update user.partner_id
      // This ensures all features that depend on partner connection update immediately
      await checkAuth();
      
      setTimeout(() => {
        fetchConnectionInfo();
      }, 1000);
    } catch (error) {
      // Show user-friendly error messages
      const errorMessage = error.response?.data?.detail || 'Connection failed. Please check the code and try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCelebrationComplete = () => {
    setShowCelebration(false);
    toast.success(`Connected with ${newPartnerName}! 💕`);
  };
  
  // CONNECTED STATE - Simplified, no code shown
  if (connectionInfo?.has_partner) {
    return (
      <MobileLayout>
        {showCelebration && (
          <CelebrationOverlay 
            partnerName={newPartnerName || connectionInfo.partner?.name?.split(' ')[0]} 
            onComplete={handleCelebrationComplete}
          />
        )}
        <div className="p-6 flex flex-col items-center justify-center min-h-[calc(100vh-120px)]" data-testid="connection-success">
          <div className="text-center space-y-4">
            <div className="w-24 h-24 gradient-brand rounded-full mx-auto flex items-center justify-center shadow-pink-glow-lg">
              <Heart className="w-12 h-12 text-white" fill="white" />
            </div>
            <h1 className="text-3xl font-heading font-bold text-gradient tracking-tight">
              Connected! 💕
            </h1>
            <p className="text-neutral-600 text-lg">
              You&apos;re connected with <span className="font-semibold text-primary">{connectionInfo.partner?.name?.split(' ')[0]}</span>
            </p>
          </div>
          
          <Button
            onClick={() => navigate('/reminders')}
            data-testid="go-to-app-btn"
            className="w-full max-w-xs h-14 rounded-full gradient-brand shadow-pink-glow-lg hover:shadow-pink-glow hover:scale-105 transition-all duration-300 mt-10"
          >
            Go to App
          </Button>
        </div>
      </MobileLayout>
    );
  }
  
  // NOT CONNECTED STATE - Show code and entry form
  return (
    <MobileLayout>
      <div className="p-6 space-y-6" data-testid="connection-setup">
        <div className="text-center space-y-3">
          <div className="w-20 h-20 gradient-brand rounded-full mx-auto flex items-center justify-center shadow-pink-glow-lg">
            <Heart className="w-10 h-10 text-white" fill="white" />
          </div>
          <h1 className="text-3xl font-heading font-bold text-gradient tracking-tight">
            Connect with Your Partner
          </h1>
          <p className="text-neutral-600">
            Share your code or enter your partner&apos;s code to unlock couple features
          </p>
        </div>
        
        <div className="bg-white rounded-3xl border border-neutral-100 shadow-soft p-6 space-y-5">
          <div className="text-center space-y-3">
            <p className="text-sm font-semibold text-neutral-500 uppercase tracking-widest">
              Your Connection Code
            </p>
            <div className="text-5xl font-heading font-bold text-gradient tracking-wider">
              {connectionCode || '------'}
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={copyCode}
                data-testid="copy-code-btn"
                variant="outline"
                className="flex-1 h-11 rounded-full border-2 border-primary/20 hover:bg-secondary transition-all"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy
              </Button>
              <Button
                onClick={shareWhatsApp}
                data-testid="share-whatsapp-btn"
                className="flex-1 h-11 rounded-full bg-[#25D366] hover:bg-[#20BD5A] text-white transition-all"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Share
              </Button>
            </div>
          </div>
          
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-neutral-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-neutral-500 font-semibold">OR</span>
            </div>
          </div>
          
          <div className="space-y-3">
            <p className="text-sm font-semibold text-neutral-500 uppercase tracking-widest text-center">
              Enter Partner&apos;s Code
            </p>
            <Input
              type="text"
              placeholder="000000"
              data-testid="partner-code-input"
              value={partnerCode}
              onChange={(e) => setPartnerCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="text-center text-3xl font-heading font-bold tracking-widest h-14 rounded-2xl"
              maxLength={6}
            />
            <Button
              onClick={connectPartner}
              data-testid="connect-btn"
              disabled={loading || partnerCode.length !== 6}
              className="w-full h-12 rounded-full gradient-brand shadow-pink-glow-lg hover:shadow-pink-glow hover:scale-105 transition-all duration-300 active:scale-95"
            >
              {loading ? 'Connecting...' : 'Connect'}
            </Button>
          </div>
        </div>
        
        <Button
          onClick={() => navigate('/reminders')}
          variant="ghost"
          className="w-full text-neutral-500"
        >
          Skip for now
        </Button>
      </div>
    </MobileLayout>
  );
};

const Connection = () => {
  return (
    <ProtectedRoute>
      <ConnectionContent />
    </ProtectedRoute>
  );
};

export default Connection;
